/**
 * Cluster Management Tab
 * 集群管理组件 - 点击"查看详情"时触发连通性检测，对齐 ArgoCD CONNECTION STATUS 风格
 * kubeconfig 获取仅限 yunwei-super-admin 角色
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Snackbar,
  Tooltip,
  Divider,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import GetAppIcon from '@material-ui/icons/GetApp';
import RefreshIcon from '@material-ui/icons/Refresh';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import { Alert } from '@material-ui/lab';
import { StatusOK, StatusError } from '@backstage/core-components';
import { useApi, identityApiRef, configApiRef } from '@backstage/core-plugin-api';
import { ClusterList } from './ClusterList';
import { RbacCheckResult } from './RbacCheckResult';
import { FixSuggestion } from './FixSuggestion';
import { useClusterManagement } from './useClusterManagement';

const useStyles = makeStyles(theme => ({
  summaryBar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    background: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2),
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  metaLabel: {
    fontWeight: 600,
    fontSize: '0.82rem',
    color: theme.palette.text.secondary,
    minWidth: 90,
  },
  metaValue: {
    fontFamily: 'monospace',
    fontSize: '0.85rem',
  },
}));

export const ClusterManagementTab: React.FC = () => {
  const classes = useStyles();
  const {
    clusters,
    loading,
    error,
    checkLoading,
    connectivity,
    connectivityLoading,
    fetchClusters,
    fetchConnectivity,
    checkCluster,
    getCheckResult,
    getKubeconfig,
  } = useClusterManagement();

  const identityApi = useApi(identityApiRef);
  const configApi = useApi(configApiRef);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailResult, setDetailResult] = useState<import('./types').ClusterCheckResult | null>(null);
  const [detailClusterName, setDetailClusterName] = useState('');
  const [kubeconfigDialogOpen, setKubeconfigDialogOpen] = useState(false);
  const [kubeconfigContent, setKubeconfigContent] = useState('');
  const [kubeconfigClusterName, setKubeconfigClusterName] = useState('');
  const [kubeconfigLoading, setKubeconfigLoading] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');
  const [canGetKubeconfig, setCanGetKubeconfig] = useState(false);

  // 加载集群列表 + 自动连通性检测
  useEffect(() => {
    fetchClusters();
    fetchConnectivity();
  }, [fetchClusters, fetchConnectivity]);

  // 检查是否有 yunwei-super-admin 角色
  useEffect(() => {
    const checkKubeconfigPermission = async () => {
      try {
        const permissionEnabled = configApi.getOptionalBoolean('permission.enabled') ?? false;
        if (!permissionEnabled) {
          setCanGetKubeconfig(true);
          return;
        }

        const identity = await identityApi.getBackstageIdentity();
        const refs = identity.ownershipEntityRefs || [];
        const hasRole = refs.some(
          ref => ref.toLowerCase() === 'group:default/yunwei-super-admin',
        );
        const userRef = identity.userEntityRef;
        const isSpecial = ['user:default/backstage', 'user:default/test456'].some(
          s => userRef?.toLowerCase() === s,
        );
        setCanGetKubeconfig(hasRole || isSpecial);
      } catch {
        setCanGetKubeconfig(false);
      }
    };
    checkKubeconfigPermission();
  }, [identityApi, configApi]);

  // 重新检测（在 Dialog 内使用）
  const handleRecheck = async () => {
    if (!detailClusterName) return;
    setDetailResult(null);
    const result = await checkCluster(detailClusterName, true);
    if (result) setDetailResult(result);
  };

  // 直接打开详情弹窗，如果没有缓存则自动触发检测
  const handleViewDetail = (clusterName: string) => {
    setDetailClusterName(clusterName);
    setDetailOpen(true);
    const cachedResult = getCheckResult(clusterName);
    if (cachedResult) {
      setDetailResult(cachedResult);
    } else {
      setDetailResult(null);
      checkCluster(clusterName, true).then(result => {
        if (result) setDetailResult(result);
      });
    }
  };

  const handleGetKubeconfig = async (clusterName: string) => {
    setKubeconfigLoading(true);
    setKubeconfigClusterName(clusterName);
    setKubeconfigDialogOpen(true);
    const content = await getKubeconfig(clusterName);
    setKubeconfigContent(content || '# 获取 kubeconfig 失败');
    setKubeconfigLoading(false);
  };

  const handleCopyKubeconfig = () => {
    navigator.clipboard.writeText(kubeconfigContent);
    setSnackMessage('kubeconfig 已复制到剪贴板');
  };

  const handleDownloadKubeconfig = () => {
    const blob = new Blob([kubeconfigContent], { type: 'text/yaml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kubeconfig-${kubeconfigClusterName}.yaml`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      {error && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}

      <ClusterList
        clusters={clusters}
        loading={loading}
        onRefresh={() => { fetchClusters(); fetchConnectivity(); }}
        onViewDetail={handleViewDetail}
        connectivity={connectivity}
        connectivityLoading={connectivityLoading}
      />

      {/* 集群详情 Dialog */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle disableTypography style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            🔍 集群详情: {detailResult?.clusterTitle || detailResult?.clusterName || detailClusterName}
          </Typography>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <Tooltip title="重新检测">
              <span>
                <IconButton
                  size="small"
                  onClick={handleRecheck}
                  disabled={checkLoading}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <IconButton size="small" onClick={() => setDetailOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {/* 检测中状态 */}
          {!detailResult && checkLoading && (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" p={6} style={{ gap: 16 }}>
              <CircularProgress size={36} />
              <Typography color="textSecondary">正在检测集群连通性与权限...</Typography>
            </Box>
          )}

          {detailResult ? (
            <>
              {/* —— 汇总 Banner —— */}
              <Box className={classes.summaryBar}>
                {/* SA / URL */}
                <Box flex={1} minWidth={280}>
                  {detailResult.serviceAccount && (
                    <Box className={classes.metaRow} mb={0.5}>
                      <Typography className={classes.metaLabel}>Service Account</Typography>
                      <Typography className={classes.metaValue}>
                        {detailResult.serviceAccount.namespace}/{detailResult.serviceAccount.name}
                      </Typography>
                    </Box>
                  )}
                  <Box className={classes.metaRow} mb={0.5}>
                    <Typography className={classes.metaLabel}>集群 URL</Typography>
                    <Typography className={classes.metaValue} style={{ wordBreak: 'break-all' }}>
                      {detailResult.clusterUrl}
                    </Typography>
                  </Box>
                  <Box className={classes.metaRow}>
                    <Typography className={classes.metaLabel}>检测时间</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {new Date(detailResult.checkedAt).toLocaleString('zh-CN')}
                    </Typography>
                  </Box>
                </Box>

                <Divider orientation="vertical" flexItem />

                {/* 连通性 —— ArgoCD 风格 */}
                <Box>
                  <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                    CONNECTION STATUS
                  </Typography>
                  {detailResult.connectionStatus === 'connected' ? (
                    <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                      <CheckCircleIcon style={{ fontSize: 18, color: '#4caf50' }} />
                      <Typography style={{ color: '#4caf50', fontWeight: 600, fontSize: '0.9rem' }}>Successful</Typography>
                    </Box>
                  ) : (
                    <Box>
                      <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                        <ErrorIcon style={{ fontSize: 18, color: '#f44336' }} />
                        <Typography style={{ color: '#f44336', fontWeight: 600, fontSize: '0.9rem' }}>Failed</Typography>
                      </Box>
                      {detailResult.connectionError && (
                        <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 2 }}>
                          {detailResult.connectionError}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>

                {/* 权限检测统计 */}
                {detailResult.permissions.length > 0 && (
                  <>
                    <Divider orientation="vertical" flexItem />
                    <Box>
                      <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                        权限检测
                      </Typography>
                      <Box display="flex" style={{ gap: 8 }}>
                        <StatusOK>
                          {detailResult.permissions.filter(p => p.status === 'ok').length} 完整
                        </StatusOK>
                        <StatusError>
                          {detailResult.permissions.filter(p => p.status === 'partial' || p.status === 'missing').length} 缺失
                        </StatusError>
                      </Box>
                    </Box>
                  </>
                )}

                {/* kubeconfig 按钮 */}
                {canGetKubeconfig && (
                  <>
                    <Divider orientation="vertical" flexItem />
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<GetAppIcon />}
                      onClick={() => handleGetKubeconfig(detailResult.clusterName)}
                      style={{ textTransform: 'none', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                    >
                      获取 Kubeconfig
                    </Button>
                  </>
                )}
              </Box>

              {/* —— 权限检测（内部含 Cluster-Scoped / ns Tabs）—— */}
              <RbacCheckResult result={detailResult} />

              {/* —— 修复建议 —— */}
              {detailResult.fixSuggestion && (
                <Box mt={3}>
                  <FixSuggestion
                    yaml={detailResult.fixSuggestion}
                    clusterName={detailResult.clusterName}
                  />
                </Box>
              )}
            </>
          ) : (
            !checkLoading && (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" p={4} style={{ gap: 12 }}>
                <Typography color="textSecondary">检测失败，请点击重新检测</Typography>
                <Button variant="outlined" size="small" onClick={handleRecheck}>
                  重新检测
                </Button>
              </Box>
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)} color="primary">
            关闭
          </Button>
        </DialogActions>
      </Dialog>

      {/* Kubeconfig Dialog */}
      <Dialog
        open={kubeconfigDialogOpen}
        onClose={() => setKubeconfigDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle disableTypography style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">📋 Kubeconfig — {kubeconfigClusterName}</Typography>
          <Box>
            <Tooltip title="复制到剪贴板">
              <IconButton size="small" onClick={handleCopyKubeconfig} disabled={kubeconfigLoading}>
                <FileCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="下载文件">
              <IconButton size="small" onClick={handleDownloadKubeconfig} disabled={kubeconfigLoading}>
                <GetAppIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={() => setKubeconfigDialogOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {kubeconfigLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <Typography color="textSecondary">生成中...</Typography>
            </Box>
          ) : (
            <pre style={{
              background: '#1e1e1e',
              color: '#d4d4d4',
              padding: 16,
              borderRadius: 4,
              fontSize: '0.8rem',
              lineHeight: 1.5,
              overflow: 'auto',
              maxHeight: 500,
              margin: 0,
            }}>
              {kubeconfigContent}
            </pre>
          )}
          {!kubeconfigLoading && (
            <Box mt={2} p={1.5} style={{
              background: '#f5f5f5',
              borderRadius: 4,
              border: '1px solid #e0e0e0',
            }}>
              <Typography variant="caption" style={{ fontWeight: 600, color: '#666' }}>
                📌 Pipeline 凭证创建命令（在 Tekton 集群执行）:
              </Typography>
              <Box
                mt={0.5}
                p={1}
                style={{
                  background: '#263238',
                  color: '#aed581',
                  borderRadius: 4,
                  fontSize: '0.75rem',
                  fontFamily: 'monospace',
                  cursor: 'pointer',
                  position: 'relative',
                }}
                onClick={() => {
                  const cmd = `kubectl create secret generic ${kubeconfigClusterName}-config --from-file=kubeconfig=$HOME/Downloads/kubeconfig-${kubeconfigClusterName}.yaml -n tekton-pipelines`;
                  navigator.clipboard.writeText(cmd);
                  setSnackMessage('创建命令已复制');
                }}
              >
                <code>
                  kubectl create secret generic {kubeconfigClusterName}-config \<br />
                  &nbsp;&nbsp;--from-file=kubeconfig=$HOME/Downloads/kubeconfig-{kubeconfigClusterName}.yaml \<br />
                  &nbsp;&nbsp;-n tekton-pipelines
                </code>
                <Typography
                  variant="caption"
                  style={{
                    position: 'absolute',
                    top: 4,
                    right: 8,
                    color: '#78909c',
                    fontSize: '0.65rem',
                  }}
                >
                  点击复制
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCopyKubeconfig} color="primary" startIcon={<FileCopyIcon />} disabled={kubeconfigLoading}>
            复制
          </Button>
          <Button onClick={handleDownloadKubeconfig} color="primary" startIcon={<GetAppIcon />} disabled={kubeconfigLoading}>
            下载
          </Button>
          <Button onClick={() => setKubeconfigDialogOpen(false)}>
            关闭
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={!!snackMessage}
        autoHideDuration={2000}
        onClose={() => setSnackMessage('')}
        message={snackMessage}
      />
    </>
  );
};
